import { Icon, IconButton } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import getUserProfile from "js/core/services/userProfiles";
import { Thumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import moment from "moment";
import React, { Component } from "react";
import styled, { css } from "styled-components";

import { getCjkUiFontFamilyCss } from "common/fontConstants";
import { IWorkspaceResource, IWorkspaceResourceFolder, PaginatedSort } from "common/interfaces";
import PresentationLibraryPaneController, { PresentationLibraryFilter, PresentationLibraryPaneControllerState, PresentationLibraryPresentation } from "js/controllers/PresentationLibraryPaneController";
import WorkspaceController from "js/controllers/WorkspaceController";
import { getPresentation } from "js/core/models/presentation";
import AvatarStack, { AvatarStackProfile, getMaxVisibleAvatarsForWidth } from "js/editor/PresentationLibrary/components/AvatarStack";
import { FolderLocationBreadcrumb } from "js/editor/PresentationLibrary/components/FolderLocationBreadcrumb";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { getPresentationContextMenuItems } from "js/editor/PresentationLibrary/PresentationContextMenu";
import { fetchPresentationCollaboratorProfiles } from "js/editor/PresentationLibrary/utils/fetchPresentationCollaboratorProfiles";
import { getLastAccessedLabel, LastActivityTooltipStyles } from "js/editor/PresentationLibrary/utils/getLastAccessedLabel";
import { isCompactLibrary } from "js/editor/PresentationLibrary/utils/isCompactLibrary";
import { app } from "js/namespaces";
import Avatar from "js/react/components/Avatar";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import LoadableAsync from "js/react/components/LoadableAsync";
import { ContextMenu, IconMenu } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { ShareDialog } from "js/react/views/PresentationSettings/dialogs/ShareDialog";
import { mouseDragItems } from "js/react/views/SharedResources/utils/mouseDragHelper";
import { _ } from "js/vendor";
import ReadOnlyBadge from "./components/ReadOnlyBadge";

export const LIBRARY_SCROLLBAR_GUTTER_WIDTH = 17;

const sharedLibraryListLayout = css`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 80px minmax(50px, 3fr) minmax(60px, 2fr) minmax(60px, 1fr) minmax(40px, 2fr) minmax(50px, 1fr) minmax(50px, 1fr) 40px;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  column-gap: 20px;

  & > * {
    min-width: 0;
  }

  @media (max-width: 1200px) {
    column-gap: 16px;
  }
`;

const LIST_MIN_VISIBLE_AVATARS = 0;
const LIST_MAX_VISIBLE_AVATARS = 5;
const LIST_PROFILE_REQUESTS = LIST_MAX_VISIBLE_AVATARS + 2;
const LIST_AVATAR_STACK_RIGHT_BUFFER = 10;

const LibraryListRoot = styled.div<{ $showHeader?: boolean }>`
  width: 100%;
  margin-top: ${props => props.$showHeader ? "10px" : "0"};
`;

const LibraryListHeader = styled.div<{ $detached?: boolean, $width?: number }>`
  ${sharedLibraryListLayout}
  width: ${props => props.$width ? `${props.$width}px` : props.$detached ? `calc(100% - ${LIBRARY_SCROLLBAR_GUTTER_WIDTH}px)` : "100%"};
  background: #E9E9E9;
  color: #666;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.5px;
  line-height: 14px;
  margin-bottom: ${props => props.$detached ? "2px" : "0"};
  padding-top: 5px;
  padding-bottom: 5px;
  padding-right: 10px;
  padding-left: 10px;
  text-transform: uppercase;
`;

const LibraryListContainer = styled.div`
    height: 100%;

    .dark-mode & {
        border-top: 2px solid #222;
    }
`;

const LibraryListItem = styled.div <{ selected: boolean; readOnly: boolean }>`
  ${sharedLibraryListLayout}
  background: ${props => !isCompactLibrary() && props.selected ? themeColors.lightBlue : "white"};
  border-bottom: 2px solid #EEF1F3;
  padding: 8px 10px;
  letter-spacing: 0px;
  ${({ readOnly = false }) => !isCompactLibrary() && !readOnly && css<{ selected: boolean }>`
    &:hover {
        background: ${props => props.selected ? themeColors.lightBlue : "#F8FAFB"};
        box-shadow: inset 2px 0 0 ${themeColors.ui_blue};
        position: relative;
        z-index: 1;
    }`}
  ${() => isCompactLibrary() && css`
    &:active {
        background: #F8FAFB;
    }
  `}
  .thumbnail {
    width: 80px;
    height: 45px;
    border-radius: 1px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    &:hover {
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12) !important;
    }
    img {
      height: 100%;
      border-radius: 1px;
    }
  }
  .title {
    color: #444;
    font-size: 14px;
    line-height: 18px;
    font-weight: 600;
    font-family: "Source Sans Pro", ${getCjkUiFontFamilyCss()};
    ${() => !isCompactLibrary() && css`
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    `}
    ${() => isCompactLibrary() && css`
        display: -webkit-box;
        overflow: hidden;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
    `}
  }
  .col {
    display: flex;
    align-items: center;
    min-width: 0;
    color: #666;
    font-size: 13px;
    line-height: 16px;
  }
  .avatar {
    width: 24px;
    height: 24px;
    margin-right: 10px;
    flex-shrink: 0;
  }

  .dark-mode & {
    background: ${props => !isCompactLibrary() && props.selected ? "#444" : "#333"};
    border-bottom: 2px solid #222;

    ${({ readOnly = false }) => !isCompactLibrary() && !readOnly && css<{ selected: boolean }>`
    &:hover {
        background: ${props => props.selected ? "#444" : "#555"};
    }`}
    ${() => isCompactLibrary() && css`
    &:active {
        background: #444;
    }`}
  }
`;

const StyledListIconMenu = styled(IconMenu)`
    &&& {
        border-radius: 4px;
        color: #666;
        cursor: pointer;
        transition: color 0.15s ease;

        &:hover {
            background-color: transparent;
            color: ${themeColors.ui_blue};
        }

        .MuiIcon-root {
            color: inherit;
        }
    }
`;

const Placeholder = styled.div`
  width: 100%;
  height: 45px;
`;

const LastAccessedLabelText = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CreatorName = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
`;

const CreatorAvatar = styled(Avatar)`
  &&& {
    cursor: default;
    background: transparent;

    img {
      display: block;
      object-fit: cover;
    }

    i,
    .micon {
      width: 24px;
      height: 24px;
      font-size: 17px;
      line-height: 24px;
    }
  }
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 15px;
  min-width: 0;

  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 2px;
    align-items: stretch;

    .title {
      min-width: 0;
    }
  }
`;

const MobileListActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  min-width: 0;
`;

const LibraryListItemSkeletonContainer = styled.div`
    height: 67px;

    .MuiSkeleton-root {
        height: 100%;
    }
`;

type PresentationLibraryListProps = PresentationLibraryPaneControllerState & {
    onSelectedPresentations?: (presentations: PresentationLibraryPresentation[]) => void;
    onDoubleClick?: (presentation: PresentationLibraryPresentation) => void;
    onStartDragItems?: (items: PresentationLibraryPresentation[]) => void;
    onDropDragItems?: (items: PresentationLibraryPresentation[]) => void;
    readOnly?: boolean;
    resourceFolders?: IWorkspaceResourceFolder[];
    onNavigateToFolder?: (folderId: string, subFolderId?: string) => void;
    showHeader?: boolean;
}

type PresentationLibraryListHeaderProps = {
    sort: PresentationLibraryPaneControllerState["sort"];
    search: PresentationLibraryPaneControllerState["search"];
    detached?: boolean;
    width?: number;
}

export function PresentationLibraryListHeader({ sort, search, detached = false, width }: PresentationLibraryListHeaderProps) {
    let currentSort = sort[0];
    if (search.query) {
        currentSort = null;
    }

    const handleSort = (sort: PaginatedSort) => {
        PresentationLibraryPaneController.setSort([sort]);
    };

    return (
        <LibraryListHeader $detached={detached} $width={width}>
            <div></div>
            <ColumnHeader
                label="Name"
                field="document.name"
                sort={currentSort}
                disabled={!!search.query}
                onSort={handleSort} />
            <ColumnHeader
                label="Edited"
                field="resource.metadata.lastActivityByType.edited.createdAt"
                sort={currentSort}
                disabled={!!search.query}
                onSort={handleSort} />
            <ColumnHeader
                label="Created"
                field="document.createdAt"
                sort={currentSort}
                disabled={!!search.query}
                onSort={handleSort} />
            <ColumnHeaderDiv>
                Folders
            </ColumnHeaderDiv>
            <ColumnHeaderDiv>
                Shared
            </ColumnHeaderDiv>
            <ColumnHeader
                label="Creator"
                field="resource.owner.name"
                sort={currentSort}
                disabled={!!search.query}
                onSort={handleSort} />
            <div />
        </LibraryListHeader>
    );
}

export const PresentationLibraryList = WorkspaceController.withState(PresentationLibraryPaneController.withState(
    class PresentationLibraryList extends Component<PresentationLibraryListProps, {
        selection: PresentationLibraryPresentation[];
        showContextMenu: boolean;
        contextMenuX: number;
        contextMenuY: number;
    }> {
        listRef: React.RefObject<HTMLDivElement>;
        startSelectionIndex: number;
        dragStarted: boolean;
        itemWasSelectedOnMouseDown: boolean;
        mouseDownSelection: PresentationLibraryPresentation[];

        constructor(props: PresentationLibraryListProps) {
            super(props);

            this.state = {
                selection: [],
                showContextMenu: false,
                contextMenuX: 0,
                contextMenuY: 0
            };

            this.listRef = React.createRef();
            this.dragStarted = false;
            this.itemWasSelectedOnMouseDown = false;
            this.mouseDownSelection = [];
        }

        getCurrentSelection = () => {
            const presentations = PresentationLibraryPaneController.legacyData;
            return this.state.selection.map(selectedPresentation =>
                presentations.find(presentation => presentation.id === selectedPresentation.id) ?? selectedPresentation
            );
        }

        isSelected = (selection: PresentationLibraryPresentation[], item: PresentationLibraryPresentation) => {
            return selection.some(selectedItem => selectedItem.id === item.id);
        }

        handleItemVisibilityChange = (visible: boolean, presentationId: string) => {
            const { data, fetching } = this.props;

            if (!visible || fetching) {
                return;
            }

            if (!PresentationLibraryPaneController.canLoadMore) {
                return;
            }

            const index = data.findIndex(reord => reord.document.id === presentationId);
            if (index === data.length - 1) {
                PresentationLibraryPaneController.loadMore();
            }
        }

        handleMouseDownOnItem = (item: PresentationLibraryPresentation, event: React.MouseEvent<HTMLDivElement>) => {
            const { readOnly } = this.props;

            const presentations = PresentationLibraryPaneController.legacyData;
            let selection = this.getCurrentSelection();

            if (readOnly) {
                return;
            }

            event.stopPropagation();
            this.itemWasSelectedOnMouseDown = this.isSelected(selection, item);

            if (event.metaKey || event.ctrlKey) {
                if (this.isSelected(selection, item)) {
                    selection = selection.filter(i => i.id !== item.id);
                } else {
                    selection = [...selection, item];
                }
            } else if (event.shiftKey) {
                const startIndex = this.startSelectionIndex ?? 0;
                const endIndex = presentations.findIndex(presentation => presentation.id === item.id);

                if (endIndex > startIndex) {
                    for (let i = startIndex + 1; i <= endIndex; i++) {
                        let item = presentations[i];
                        if (!this.isSelected(selection, item)) {
                            selection = [...selection, item];
                        }
                    }
                } else {
                    for (let i = startIndex; i >= endIndex; i--) {
                        let item = presentations[i];
                        if (!this.isSelected(selection, item)) {
                            selection = [...selection, item];
                        }
                    }
                }
            } else {
                if (!this.isSelected(selection, item)) {
                    selection = [item];
                    // Match by id, not object reference: `item` isn't the same reference as the
                    // entry in `presentations` (legacyData), so indexOf returns -1 and the shift-range
                    // anchor falls back to the top of the list.
                    this.startSelectionIndex = presentations.findIndex(presentation => presentation.id === item.id);
                }
                this.dragItems(event, item, selection);
            }

            this.mouseDownSelection = selection;
            this.setState({ selection });
        }

        handleMouseUpOnItem = (item: PresentationLibraryPresentation, event: React.MouseEvent<HTMLDivElement>) => {
            const { onSelectedPresentations, readOnly } = this.props;
            const selection = this.getCurrentSelection();
            const didDrag = this.dragStarted;
            const itemWasSelectedOnMouseDown = this.itemWasSelectedOnMouseDown;
            const isDeselect = itemWasSelectedOnMouseDown && !didDrag && selection.length === 1 && this.isSelected(selection, item);

            if (readOnly) {
                if (!isDeselect) {
                    onSelectedPresentations?.([item]);
                }
                return;
            }

            event.preventDefault();

            if (event.shiftKey || event.metaKey || event.ctrlKey) {
                onSelectedPresentations?.(this.mouseDownSelection);
                return;
            }

            if (!isDeselect) {
                onSelectedPresentations?.([item]);
            }

            this.setState({ selection: isDeselect ? [] : [item] });
        }

        dragItems(event, mousedItem, selection) {
            this.dragStarted = false;

            mouseDragItems({
                event,
                container: this.listRef.current,
                mousedItem,
                selection,
                imageBorderColor: "#333",
                onDidStartDrag: () => {
                    this.dragStarted = true;
                },
                onStartDragItems: this.props.onStartDragItems,
                onDropDragItems: this.props.onDropDragItems,
            });
        }

        handleContextMenu = (item: PresentationLibraryPresentation, event: React.MouseEvent<HTMLDivElement>) => {
            const { readOnly } = this.props;
            if (app.isMobileOrTablet) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            if (readOnly) {
                return;
            }

            const selection = this.getCurrentSelection();
            const nextSelection = this.isSelected(selection, item) ? selection : [item];

            event.preventDefault();
            this.setState({
                selection: nextSelection,
                showContextMenu: true,
                contextMenuX: event.pageX,
                contextMenuY: event.pageY
            });

            if (nextSelection !== selection) {
                this.props.onSelectedPresentations?.([item]);
            }
        }

        handleCloseContextMenu = () => {
            this.setState({ showContextMenu: false });
        }

        handleMenuClickOnItem = (item: PresentationLibraryPresentation) => {
            this.setState({ selection: [item] });
            this.props.onSelectedPresentations?.([item]);
        }

        async loadPermission(selection) {
            if (selection.length === 1) {
                let item = selection[0];
                await item?.presentationModel?.getUserPermissions(true);
            } else {
                return;
            }
        }

        render() {
            const { sort, readOnly, onDoubleClick, showHeader = !isCompactLibrary(), resourceFolders, onNavigateToFolder, fetching, search, data } = this.props;
            const { showContextMenu, contextMenuX, contextMenuY } = this.state;

            const selection = this.getCurrentSelection();
            const filter = PresentationLibraryPaneController.libraryFilter;
            const presentations = PresentationLibraryPaneController.legacyData;

            return (
                <LibraryListRoot className="library-list" ref={this.listRef} $showHeader={showHeader && !isCompactLibrary()}>
                    <LastActivityTooltipStyles />
                    {showHeader && !isCompactLibrary() &&
                        <PresentationLibraryListHeader sort={sort} search={search} />
                    }
                    <LibraryListContainer>
                        {presentations.map((presentation, idx) => (
                            <PresentationLibraryListItem
                                key={`${presentation.id}:${idx}`}
                                item={presentation}
                                readOnly={readOnly}
                                selected={this.isSelected(selection, presentation)}
                                onMouseDown={event => this.handleMouseDownOnItem(presentation, event)}
                                onMouseUp={event => this.handleMouseUpOnItem(presentation, event)}
                                onDoubleClick={onDoubleClick}
                                onContextMenu={event => this.handleContextMenu(presentation, event)}
                                filter={filter}
                                resourceFolders={resourceFolders}
                                resource={data.find(d => d.document.id === presentation.id)?.resource}
                                onNavigateToFolder={onNavigateToFolder}
                                onVisibleChange={this.handleItemVisibilityChange}
                                onMenuClick={this.handleMenuClickOnItem}
                            />
                        ))}
                        {fetching &&
                            <PresentationLibraryListItemSkeleton />
                        }
                    </LibraryListContainer>

                    {showContextMenu &&
                        <LoadableAsync
                            load={() => this.loadPermission(selection)}
                            renderChildren={() => (
                                <ContextMenu
                                    // @ts-ignore
                                    x={contextMenuX}
                                    y={contextMenuY}
                                    onClose={this.handleCloseContextMenu}
                                >
                                    {getPresentationContextMenuItems({ selectedPresentations: selection })}
                                </ContextMenu>
                            )}
                        />
                    }
                </LibraryListRoot>
            );
        }
    }
));

const ColumnHeaderDiv = styled.div<{ $sortable?: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${props => props.$sortable ? "pointer" : "default"};

  font-family: "Source Sans Pro";
  font-size: inherit;
  font-style: normal;
  font-weight: inherit;
  letter-spacing: inherit;
  line-height: inherit;
  text-transform: uppercase;
  
  .MuiIcon-root {
    font-size: 14px;
    margin-left: 6px;
    color: #666;
  }

  .sort-preview {
    opacity: 0;
  }

  &:hover .sort-preview {
    opacity: ${props => props.$sortable ? 1 : 0};
  }

  &:hover .MuiIcon-root {
    color: ${themeColors.ui_blue};
  }
`;

class ColumnHeader extends Component<{
    label: string;
    field: string;
    sort?: PaginatedSort;
    onSort: (sort: PaginatedSort) => void;
    disabled?: boolean;
}> {
    handleClick = event => {
        const { field, sort, onSort, disabled } = this.props;
        if (disabled) {
            return;
        }
        const isSorted = sort?.field === field;
        // Toggle direction only when re-clicking the active column. When switching to a new
        // column, sort descending to match the down-arrow shown on hover (line ~602).
        onSort({ field, direction: isSorted ? (sort?.direction === "asc" ? "desc" : "asc") : "desc" });
    }

    render() {
        const { label, field, sort, disabled } = this.props;
        const isSorted = sort?.field === field;

        return (
            <ColumnHeaderDiv onClick={this.handleClick} $sortable={!disabled}>
                {label}
                {isSorted && sort?.direction === "asc" && <Icon>arrow_upward</Icon>}
                {isSorted && sort?.direction === "desc" && <Icon>arrow_downward</Icon>}
                {!isSorted && !disabled && <Icon className="sort-preview">arrow_downward</Icon>}
            </ColumnHeaderDiv>
        );
    }
}

interface PresentationLibraryListItemProps {
    item: PresentationLibraryPresentation;
    readOnly: boolean;
    selected: boolean;
    onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
    onMouseUp: (event: React.MouseEvent<HTMLDivElement>) => void;
    onContextMenu: (event: React.MouseEvent<HTMLDivElement>) => void;
    onDoubleClick: (presentation: PresentationLibraryPresentation) => void;
    filter: PresentationLibraryFilter;
    resourceFolders?: IWorkspaceResourceFolder[];
    resource?: IWorkspaceResource;
    onNavigateToFolder?: (folderId: string, subFolderId?: string) => void;
    onVisibleChange?: (visible: boolean, presentationId: string) => void;
    onMenuClick?: (item: PresentationLibraryPresentation) => void;
}

class PresentationLibraryListItem extends Component<PresentationLibraryListItemProps, {
    url: string;
    ownerName: string;
    avatarUrl: string;
    visible: boolean;
    avatarProfiles: AvatarStackProfile[];
    sharedColumnWidth: number | null;
}> {
    isMounted = false;
    ref: React.RefObject<HTMLDivElement>;
    sharedColumnRef: React.RefObject<HTMLDivElement>;
    observer: IntersectionObserver;
    resizeObserver?: ResizeObserver;
    isFetchingCollaboratorProfiles = false;

    constructor(props) {
        super(props);

        this.state = {
            url: "",
            ownerName: "",
            avatarUrl: "",
            visible: false,
            avatarProfiles: [],
            sharedColumnWidth: null,
        };

        this.ref = React.createRef();
        this.sharedColumnRef = React.createRef();
    }

    componentDidMount() {
        this.isMounted = true;
        this.observer = new IntersectionObserver(entries => {
            const visible = entries[0].isIntersecting === true;
            this.props.onVisibleChange?.(visible, this.props.item.id);

            if (visible) {
                if (_.isEmpty(this.state.url)) {
                    this.fetchImages();
                }
                this.fetchCollaboratorProfiles();
                if (!this.state.visible) {
                    this.setState({ visible: true });
                }
            } else {
                if (this.state.visible) {
                    this.setState({ visible: false });
                }
            }
        });

        this.observer.observe(this.ref.current);
    }

    componentDidUpdate(prevProps: PresentationLibraryListItemProps, prevState: { visible: boolean; avatarProfiles: AvatarStackProfile[] }) {
        if (!prevState.visible && this.state.visible && this.state.avatarProfiles.length === 0) {
            this.fetchCollaboratorProfiles();
        }

        if (!prevState.visible && this.state.visible) {
            this.startObservingSharedColumn();
        }
    }

    componentWillUnmount() {
        this.isMounted = false;
        this.observer.disconnect();
        this.resizeObserver?.disconnect();
    }

    startObservingSharedColumn() {
        if (this.resizeObserver || !this.sharedColumnRef.current) {
            return;
        }

        this.resizeObserver = new ResizeObserver(entries => {
            const width = entries[0]?.contentRect.width;
            if (width) {
                this.setState({ sharedColumnWidth: width });
            }
        });
        this.resizeObserver.observe(this.sharedColumnRef.current);
    }

    async fetchCollaboratorProfiles() {
        if (this.isFetchingCollaboratorProfiles || this.state.avatarProfiles.length > 0) {
            return;
        }

        const { item } = this.props;
        this.isFetchingCollaboratorProfiles = true;
        try {
            const profiles = await fetchPresentationCollaboratorProfiles({
                workspaceId: item.workspaceId,
                resourceId: item.id,
                ownerUid: item.ownerUid,
                maxProfileRequests: LIST_PROFILE_REQUESTS,
            });
            if (this.ref.current) this.setState({ avatarProfiles: profiles });
        } catch {
            // ignore collaborator fetch errors
        } finally {
            this.isFetchingCollaboratorProfiles = false;
        }
    }

    async fetchImages() {
        const { item } = this.props;

        if (!this.isMounted) {
            return;
        }

        await Promise.all([
            (async () => {
                const url = await item.getThumbnailUrl();
                this.setState({ url });
            })(),
            (async () => {
                const { displayName, email, photoURL } = await getUserProfile(item.ownerUid);
                this.setState({
                    ownerName: displayName ?? email ?? "",
                    avatarUrl: photoURL ?? ""
                });
            })()
        ]);
    }

    handleSharePresentation = async selectedPresentation => {
        const presentation = await getPresentation(selectedPresentation.id, { permission: "write", autoSync: true });
        ShowDialog(ShareDialog, { presentation, onClose: () => presentation.disconnect() });
    }

    render() {
        const { item, readOnly, onMouseDown, onMouseUp, onContextMenu, selected, onDoubleClick, resourceFolders, onNavigateToFolder, resource, onMenuClick } = this.props;
        const { url, ownerName, avatarUrl, visible, avatarProfiles, sharedColumnWidth } = this.state;

        const lastAccessedLabel = getLastAccessedLabel(item);
        const collaboratorProfiles = avatarProfiles.filter(profile => !profile.isCreator);
        const showSingleAvatarInList = collaboratorProfiles.length === 1;
        const maxVisibleAvatars = getMaxVisibleAvatarsForWidth(
            sharedColumnWidth ? sharedColumnWidth - LIST_AVATAR_STACK_RIGHT_BUFFER : null,
            LIST_MIN_VISIBLE_AVATARS,
            LIST_MAX_VISIBLE_AVATARS
        );

        return (
            <LibraryListItem
                className="library-list-item"
                data-item-id={item.id}
                selected={selected}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onDoubleClick={() => onDoubleClick && onDoubleClick(item)}
                onContextMenu={event => {
                    if (app.isMobileOrTablet) {
                        event.preventDefault();
                        event.stopPropagation();
                        return;
                    }
                    onContextMenu(event);
                }}
                ref={this.ref}
                readOnly={readOnly}
            >
                {!visible && <Placeholder />}
                {visible && <>
                    <Thumbnail readOnly={readOnly} url={item.dirty ? null : url} showSpinner={true} />

                    <TitleRow>
                        <TruncatedTooltip title={item.name}>
                            <div className="title">{item.name}</div>
                        </TruncatedTooltip>
                        {!isCompactLibrary() && (!item.permissions.owner && !item.permissions.write) && <ReadOnlyBadge quiet />}
                    </TitleRow>

                    {
                        !isCompactLibrary() &&
                        <>
                            <div className="col">
                                <TruncatedTooltip
                                    title={lastAccessedLabel?.tooltipLabel ?? ""}
                                    tooltipClasses={{ tooltip: "last-activity-tooltip" }}
                                    enterDelay={500}
                                    enterNextDelay={500}
                                >
                                    <LastAccessedLabelText>
                                        <span>{`${lastAccessedLabel?.label ?? "—"}`}</span>
                                    </LastAccessedLabelText>
                                </TruncatedTooltip>
                            </div>
                            <div className="col">{moment(item.createdAt).format("MMM D, YYYY")}</div>
                            <div className="col" style={{ minWidth: 0, overflow: "hidden" }}>
                                {resourceFolders && resource && onNavigateToFolder && (
                                    <FolderLocationBreadcrumb
                                        resource={resource}
                                        resourceFolders={resourceFolders}
                                        onNavigateToFolder={onNavigateToFolder}
                                    />
                                )}
                            </div>
                            <div className="col" ref={this.sharedColumnRef}>
                                <AvatarStack
                                    profiles={collaboratorProfiles}
                                    showSingleAvatar={showSingleAvatarInList}
                                    minVisibleAvatars={LIST_MIN_VISIBLE_AVATARS}
                                    maxVisibleAvatars={maxVisibleAvatars}
                                />
                            </div>

                            <div className="col">
                                <CreatorAvatar url={avatarUrl} />
                                <TruncatedTooltip title={ownerName}>
                                    <CreatorName>
                                        {ownerName}
                                    </CreatorName>
                                </TruncatedTooltip>
                            </div>
                            {
                                !readOnly &&
                                <div
                                    onMouseDown={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onMouseUp={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onDoubleClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onClick={e => {
                                        e.stopPropagation();
                                    }}
                                >
                                    <StyledListIconMenu onClickCapture={() => onMenuClick?.(item)}>
                                        {getPresentationContextMenuItems({
                                            selectedPresentations: [item],
                                        })}
                                    </StyledListIconMenu>
                                </div>
                            }
                        </>
                    }
                    {
                        isCompactLibrary() &&
                        <MobileListActions>
                            {(!item.permissions.owner && !item.permissions.write) && <ReadOnlyBadge quiet />}
                            {
                                !window.isZoomApp &&
                                !readOnly &&
                                item.permissions.write &&
                                <IconButton
                                    className="text-blue"
                                    onMouseDown={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onMouseUp={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onDoubleClick={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onContextMenu={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onClick={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        this.handleSharePresentation(item);
                                    }}
                                >
                                    <Icon>share</Icon>
                                </IconButton>
                            }
                        </MobileListActions>
                    }
                </>}
            </LibraryListItem>
        );
    }
}

function PresentationLibraryListItemSkeleton() {
    return (
        <LibraryListItemSkeletonContainer>
            <Skeleton />
        </LibraryListItemSkeletonContainer>
    );
}
